import { useExcelError, useExcelErrorAry } from '@/views/editor/composable/table/useExcelErr'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { directSocketSave } from '@/views/editor/composable/useNote'
import { useSelectorOverlay } from '@/views/editor/composable/table/selector/selctorOverlay.ts'

function getColumnLabel(index: number): string {
	let label = ''
	while (index > 0) {
		const remainder = (index - 1) % 26
		label = String.fromCharCode(65 + remainder) + label
		index = Math.floor((index - 1) / 26)
	}
	return label
}

function getColumnIndex(label: string): number {
	let index = 0
	for (let i = 0; i < label.length; i++) {
		const charCode = label.toUpperCase().charCodeAt(i) - 64
		index = index * 26 + charCode
	}
	return index
}

/**
 * @param n (number) - 버려지는 수
 * @param s (significance) - 버려지는 숫자 단위
 * @constructor
 * significance 에 지정된 숫자 밑으로 버리는 함수
 * EX) FLOOR(14500, 1000) - return 14000
 */
export function FLOOR(n: number, s: number) {
	const number = Math.floor(n / s) * s

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

/**
 * @param n (number) - 자연 로그값을 게산할 양의 실수
 * @constructor
 */
export function LN(n: number) {
	const number = Math.log(n)

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

/**
 * @param n (number) - 로그값을 계산할 양의 실수
 * @param b (base) - 로그의 밑, 생략하면 10으로 간주
 * @constructor
 */
export function LOG(n: number, b: number = 10) {
	const number = Math.log(n) / Math.log(b)

	if (b === 1) {
		return useExcelError.div0
	}

	if (b < 0 || isNaN(number)) {
		return useExcelError.num
	}

	return number
}

export function MOD(n: number, d: number) {
	let number = Math.abs(n % d)
	number = n < 0 ? d - number : number

	if (isNaN(number)) {
		return useExcelError.num
	}

	return d > 0 ? number : -number
}

export function ROUND(n: number, nd: number) {
	const number = Number(Math.round(Number(n + 'e' + nd)) + 'e' + nd * -1)

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

export function SQRT(n: number) {
	const number = Math.sqrt(n)

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

export function SUM(obj: any) {
	const numbers = obj.filter((item: any) => item !== '' && item !== null && item !== undefined).map((item: any) => Number(item))

	const number = numbers.reduce((sum: number, val: number) => sum + val, 0)

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

export function AVERAGE(obj: any) {
	const numbers = obj.filter((item: any) => item !== '' && item !== null && item !== undefined).map((item: any) => Number(item))

	if (numbers.length === 0) {
		return 0
	}

	const sum = numbers.reduce((acc: number, val: number) => acc + val, 0)
	const number = sum / numbers.length

	if (isNaN(number)) {
		return useExcelError.num
	}

	return number
}

export const AVG = AVERAGE

export function COUNT(obj: any) {
	return obj.length
}

export function MAX(obj: any) {
	return Math.max.apply(null, obj)
}

export function MIN(obj: any) {
	return Math.min.apply(null, obj)
}

export function STDEV(obj: any) {
	const numbers = obj.filter((item: any) => item !== '' && item !== null && item !== undefined).map((item: any) => Number(item))

	const n = numbers.length
	if (n === 0) return 0
	if (n === 1) return 0

	const mean = numbers.reduce((sum: number, val: number) => sum + val, 0) / n

	const variance =
		numbers.reduce((sum: number, val: number) => {
			return sum + Math.pow(val - mean, 2)
		}, 0) / n

	const stdev = Math.sqrt(variance)
	return isNaN(stdev) ? 0 : stdev
}

export function CONCATENATE(obj: any) {
	return obj.join('')
}

export const CONCAT = CONCATENATE

export function LEN(cell: string) {
	return cell.length
}

export function MID(t: string, sn: number, nc: number) {
	return t.substring(sn - 1, sn - 1 + nc)
}

export function SUBSTITUTE(t: string, ot: string, nt: string, isn: number) {
	if (!t || !ot) {
		return t
	} else if (isn === undefined) {
		return t.split(ot).join(nt)
	} else {
		isn = Math.floor(Number(isn))

		if (Number.isNaN(isn) || isn <= 0) {
			return false
		}

		let index = 0
		let i = 0

		while (index > -1 && t.indexOf(ot, index) > -1) {
			index = t.indexOf(ot, index + 1)
			i++

			if (index > -1 && i === isn) {
				return t.substring(0, index) + nt + t.substring(index + ot.length)
			}
		}

		return t
	}
}

export function AND(str: string) {
	str = str.replace(/\s/gi, '')
	const strAry = str.split(',')
	let trueCount = 0

	strAry.forEach((item: any) => {
		item ? trueCount++ : trueCount
	})

	return trueCount === strAry.length
}

export function OR(str: string) {
	str = str.replace(/\s/gi, '')
	const strAry = str.split(',')
	let trueCount = 0

	strAry.forEach((item: any) => {
		item ? trueCount++ : trueCount
	})

	return trueCount >= 1
}

export function IF(str: string, t: string, f: string) {
	const tt = t ? t : 'TRUE'
	const tf = f ? f : 'FALSE'

	return str ? tt : tf
}

export function LOOKUP(v: string, a: any, ra: any) {
	const isNumberLookup = typeof v === 'number'
	let result: any

	for (let i = 0; i < a.length; i++) {
		if (a[i] === v) {
			return ra[i]
		} else if ((isNumberLookup && a[i] <= v) || (typeof a[i] === 'string' && a[i].localeCompare(v) < 0)) {
			result = ra[i]
		} else if (isNumberLookup && a[i] > v) {
			return result
		}
	}

	return result
}

export function TEXTJOIN(v: any, e: boolean = true, args: any) {
	v = v !== null && v !== undefined ? v : ''

	if (Array.isArray(v)) {
		const newAry = []
		let index = 0

		args.forEach((item: any) => {
			if (e) {
				if (item) {
					newAry.push(item)
					newAry.push(v[index])
				}
			} else {
				if (item) {
					newAry.push(item)
				} else {
					newAry.push(' ')
				}
				newAry.push(v[index])
			}

			index++

			if (index === v.length) {
				index = 0
			}
		})

		return args.join('')
	}

	return args.join(v)
}

export function FACT(n: number) {
	n = Math.floor(n)

	if (n < 0) {
		return useExcelError.num
	}

	let factNum = n

	for (let i = n - 1; i > 0; i--) {
		factNum *= i
	}

	return factNum
}

export function ISBLANK(v: any) {
	return !v
}

export function ISERROR(v: any) {
	return useExcelErrorAry.includes(v)
}

export function LEFT(s: string, n: number) {
	if (n < 1) {
		return useExcelError.value
	}
	return s.substring(0, n)
}

export function RIGHT(s: string, n: number) {
	if (n < 1) {
		return useExcelError.value
	}

	return s.substring(s.length - n)
}

export function PRODUCT(n: any) {
	return n.reduce((a: number, c: number) => {
		return a * c
	})
}

export function TRUE() {
	return 'TRUE'
}

export function FALSE() {
	return 'FALSE'
}

export function ISNUMBER(n: number) {
	if (typeof n !== 'number') {
		return 'FALSE'
	}

	return 'TRUE'
}

export function POWER(n: number, sz: number) {
	if (typeof n !== 'number' || typeof sz !== 'number') {
		return useExcelError.num
	}
	return Math.pow(n, sz)
}

export function SLOPE(x: any, y: any) {
	if (!Array.isArray(x) || !Array.isArray(y)) {
		return useExcelError.value
	}

	const xAvg = x.reduce((a: number, c: number) => a + c) / x.length
	const yAvg = y.reduce((a: number, c: number) => a + c) / y.length
	let numerator: number = 0
	let denominator: number = 0

	x.forEach((item: number, index: number) => {
		numerator += (item - xAvg) * (y[index] * yAvg)
	})

	y.forEach((item: number) => {
		denominator += (item - xAvg) * (item * xAvg)
	})

	return numerator / denominator
}

export function INTERCEPT(x: any, y: any, s: number) {
	if (!Array.isArray(x) || !Array.isArray(y)) {
		return useExcelError.value
	}

	if (typeof s !== 'number') {
		return useExcelError.num
	}

	if ((Number(s) === s && s % 1 === 0) || !(Number(s) === s && s % 1 !== 0)) {
		return useExcelError.value
	}

	const xAvg = x.reduce((a: number, c: number) => a + c) / x.length
	const yAvg = y.reduce((a: number, c: number) => a + c) / y.length

	return yAvg - s * xAvg
}

const functionMap: any = {
	FLOOR,
	LN,
	LOG,
	MOD,
	ROUND,
	SQRT,
	SUM,
	AVERAGE,
	AVG,
	COUNT,
	MAX,
	MIN,
	STDEV,
	CONCATENATE,
	LEN,
	MID,
	SUBSTITUTE,
	AND,
	OR,
	IF,
	LOOKUP,
	TEXTJOIN,
	FACT,
	ISBLANK,
	ISERROR,
	LEFT,
	RIGHT,
	PRODUCT,
	TRUE,
	FALSE,
	ISNUMBER,
	POWER,
	SLOPE,
	INTERCEPT,
}

export function functionActivePosition(c: string) {
	const position = functionActiveList(c)
	if (!position.error) {
		positionActive(position)
	}
}

export function functionActiveList(c: string) {
	const textList = c.split(',')
	const position: any = []

	for (const item of textList) {
		let cleanItem = item.trim()

		if (cleanItem.includes('=')) {
			return { error: true, type: useExcelError.value }
		}

		const nestedFunctionMatch = cleanItem.match(/^([A-Z]+)\((.*)\)$/)
		if (nestedFunctionMatch) {
			const funcName = nestedFunctionMatch[1]
			const funcArgs = nestedFunctionMatch[2]

			position.push({
				type: 'nested_function',
				functionName: funcName,
				args: funcArgs,
			})
			continue
		}

		const textItem = cleanItem.split(':')
		const positionAry = {
			type: '',
			item: [] as any,
		}

		if (textItem.length > 1) {
			positionAry.type = 'area'
		} else {
			positionAry.type = 'text'
		}

		textItem.forEach((text: string) => {
			const textAry = [...text]
			const positionObject: any = {
				row: 0,
				col: 0,
			}
			let rowCount = 0
			let rowNumber = 0
			let colLabel = ''
			textAry.forEach((tiny: string) => {
				if (isNaN(Number(tiny))) {
					colLabel += tiny
				} else {
					const textNumber = Number(tiny)
					if (rowCount > 0) {
						rowNumber = Number(rowNumber + '' + textNumber)
					} else {
						rowNumber = textNumber
					}
					rowCount++
				}
			})
			positionObject.row = rowNumber
			positionObject.col = colLabel ? getColumnIndex(colLabel) : 0

			positionAry.item.push(positionObject)
		})

		position.push(positionAry)
	}

	return position
}

function positionActiveInternal(position: any, tableSelector: any) {
	position.forEach((pos: any) => {
		if (pos.type === 'nested_function') {
			const nestedPosition = functionActiveList(pos.args)
			if (!nestedPosition.error) {
				positionActiveInternal(nestedPosition, tableSelector)
			}
			return
		} else if (pos.type === 'area') {
			let minRowIndex = Math.min(...pos.item.map((pos: any) => pos.row))
			let maxRowIndex = Math.max(...pos.item.map((pos: any) => pos.row))
			let minColIndex = Math.min(...pos.item.map((pos: any) => pos.col))
			let maxColIndex = Math.max(...pos.item.map((pos: any) => pos.col))

			if (minColIndex === 0) minColIndex = maxColIndex
			if (minRowIndex === 0) minRowIndex = maxRowIndex

			// tableSelector는 0-based이므로 1-based 좌표에서 -1
			for (let i = minRowIndex; i <= maxRowIndex; i++) {
				for (let j = minColIndex; j <= maxColIndex; j++) {
					if (!tableSelector.value[i - 1] || !tableSelector.value[i - 1][j - 1]) continue
					const cell = tableSelector.value[i - 1][j - 1].html
					const cellContenteditable = cell.querySelector('div[contenteditable="true"]')
					if (cellContenteditable) {
						cellContenteditable.classList.add('active')
					}
				}
			}
		} else {
			const rowIndex = pos.item[0].row
			const colIndex = pos.item[0].col

			if (rowIndex === 0 || colIndex === 0) return

			// tableSelector는 0-based이므로 1-based 좌표에서 -1
			if (!tableSelector.value[rowIndex - 1] || !tableSelector.value[rowIndex - 1][colIndex - 1]) return
			const cell = tableSelector.value[rowIndex - 1][colIndex - 1].html
			const cellContenteditable = cell.querySelector('div[contenteditable="true"]')
			if (cellContenteditable) {
				cellContenteditable.classList.add('active')
			}
		}
	})
}

function positionActive(position: any) {
	const { tableSelector } = storeToRefs(useEditorStore())

	tableSelector.value.forEach((row: any, rowIndex: number) => {
		row.forEach((cell: any, cellIndex: number) => {
			if (rowIndex !== 0 && cellIndex !== 0) {
				const cellContenteditable = cell.html.querySelector('div[contenteditable="true"]')
				if (cellContenteditable) {
					cellContenteditable.classList.remove('active')
				}
			}
		})
	})

	positionActiveInternal(position, tableSelector)
}

export function itemEvent(functionName: string, item: any) {
	if (!functionMap[functionName]) {
		return useExcelError.name
	}

	let result: any

	switch (functionName) {
		case 'SUM':
			result = functionMap[functionName](item)
			break

		case 'AVG':
		case 'AVERAGE':
			if (isNullCheckCell) return useExcelError.value
			if (functionName === 'AVG') {
				functionName = 'AVERAGE'
			}
			result = functionMap[functionName](item)
			break

		case 'STDEV':
			if (isNullCheckCell) return useExcelError.value
			result = functionMap[functionName](item)
			break
	}

	return result
}

export function cellPosition(target: any) {
	const { tableSelector } = storeToRefs(useEditorStore())

	let positionText: string = ''

	const overCell: any = []

	let actualRowIndex = 0
	tableSelector.value.forEach((row: any, rowIndex: number) => {
		let actualColIndex = 0
		row.forEach((cell: any, cellIndex: number) => {
			if (overCell.includes(cell.html.id)) return
			if (cell.html.classList && cell.html.classList.contains('number--line')) return
			if (cell.html.id === target.id) {
				positionText += String.fromCharCode(actualColIndex + 65).toUpperCase()
				positionText += actualRowIndex + 1
				overCell.push(cell.html.id)
			}
			actualColIndex++
		})
		if (actualColIndex > 0) actualRowIndex++
	})

	return positionText
}

export function cellMultiPosition() {
	const { dragList, tableSelector } = storeToRefs(useEditorStore())
	if (dragList.value.length < 1) return
	const positions: any = []

	dragList.value.forEach((item: any) => {
		if (item.classList && item.classList.contains('number--line')) return
		let actualRowIndex = 0
		tableSelector.value.forEach((row: any, index: number) => {
			let actualColIndex = 0
			row.forEach((cell: any, cellIndex: number) => {
				if (cell.html.classList && cell.html.classList.contains('number--line')) return
				if (cell.html.id === item.id) {
					positions.push({
						row: actualRowIndex,
						col: actualColIndex,
					})
				}
				actualColIndex++
			})
			if (actualColIndex > 0) actualRowIndex++
		})
	})

	let minRowIndex = Math.min(...positions.map((pos: any) => pos.row))
	let maxRowIndex = Math.max(...positions.map((pos: any) => pos.row))
	let minColIndex = Math.min(...positions.map((pos: any) => pos.col))
	let maxColIndex = Math.max(...positions.map((pos: any) => pos.col))

	const rePositions: any = []
	for (let i = minRowIndex; i <= maxRowIndex; i++) {
		for (let j = minColIndex; j <= maxColIndex; j++) {
			let actualRowIndex = 0
			tableSelector.value.forEach((row: any, index: number) => {
				let actualColIndex = 0
				row.forEach((cell: any, cellIndex: number) => {
					if (cell.html.classList && cell.html.classList.contains('number--line')) return
					if (i === actualRowIndex && j === actualColIndex) {
						let rows = actualRowIndex
						let cells = actualColIndex
						if (cell.html.rowSpan > 1) {
							for (let k = 0; k < cell.html.rowSpan; k++) {
								rows = actualRowIndex + k

								rePositions.push({
									row: rows,
									col: cells,
								})
							}
						}
						if (cell.html.colSpan > 1) {
							for (let k = 1; k < cell.html.cellIndex - 1; k++) {
								cells = actualColIndex + k

								rePositions.push({
									row: rows,
									col: cells,
								})
							}
						}
						rePositions.push({
							row: rows,
							col: cells,
						})
					}
					actualColIndex++
				})
				if (actualColIndex > 0) actualRowIndex++
			})
		}
	}

	minRowIndex = Math.min(...rePositions.map((pos: any) => pos.row))
	maxRowIndex = Math.max(...rePositions.map((pos: any) => pos.row))
	minColIndex = Math.min(...rePositions.map((pos: any) => pos.col))
	maxColIndex = Math.max(...rePositions.map((pos: any) => pos.col))

	const minPositionText = String.fromCharCode(minColIndex + 65).toUpperCase() + (minRowIndex + 1)
	const maxPositionText = String.fromCharCode(maxColIndex + 65).toUpperCase() + (maxRowIndex + 1)

	return minPositionText + ':' + maxPositionText
}

let isNullCheckCell = false

export function calcFunction() {
	isNullCheckCell = false
	const { tableSelector, cellTarget } = storeToRefs(useEditorStore())

	if (!tableSelector.value) return

	tableSelector.value.forEach((row: any) => {
		row.forEach((cell: any) => {
			if (cell.html.dataset.type === 'function') {
				const functionName = cell.html.dataset.function
				let value: any = []
				const functionPosition = cell.html.dataset.position
				const position = functionActiveList(functionPosition)

				if (position.error) {
					tableSelector.value.forEach((row: any) => {
						row.forEach((cell: any) => {
							const cellContenteditable = cell.html.querySelector('div[contenteditable="true"]')
							if (cellContenteditable) {
								cellContenteditable.classList.remove('active')
							}
						})
					})

					const target = cell.html.querySelector('div')
					target.setAttribute('contenteditable', 'false')
					target.innerHTML = `<span>${position.type}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
					return
				}

				position.forEach((pos: any) => {
					pos.item.forEach((item: any) => {
						if (tableSelector.value[item.row - 1] && tableSelector.value[item.row - 1][item.col - 1]) {
							if (tableSelector.value[item.row - 1][item.col - 1].html.querySelector('div').innerHTML === '') {
								isNullCheckCell = true
							}
							const target = tableSelector.value[item.row - 1][item.col - 1].html.querySelector('div')
							target.classList.remove('active')
							value.push(tableSelector.value[item.row - 1][item.col - 1].html.innerText)
						} else {
							isNullCheckCell = true
						}
					})
				})

				const target = cell.html.querySelector('div')
				target.setAttribute('contenteditable', 'false')
				target.innerHTML = `<span>${itemEvent(functionName, value)}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`

				const useCreate = useCreateTable()
				useCreate.event()
				directSocketSave('cell', cell.html)
			}
		})
	})
}

export function activeFunction() {
	const { cellTarget, functionTarget, isFunctionActive } = storeToRefs(useEditorStore())

	if (functionTarget.value && !cellTarget.value && functionTarget.value.dataset.type === 'function') {
		cellTarget.value = functionTarget.value
	}

	if (!cellTarget.value) {
		console.warn('[activeFunction] cellTarget이 null입니다')
		return
	}

	const functionName = cellTarget.value.dataset.function
	const functionPosition = cellTarget.value.dataset.position

	const target = cellTarget.value.querySelector('div')

	if (!target) {
		console.warn('[activeFunction] div 요소를 찾을 수 없습니다')
		return
	}

	isFunctionActive.value = true

	target.setAttribute('contenteditable', 'true')
	target.innerHTML = `=${functionName}(${functionPosition})`

	const selection = window.getSelection()
	if (selection) {
		selection.removeAllRanges()

		// 커서를 닫는 괄호 앞으로 이동
		if (target.firstChild) {
			const range = document.createRange()
			const text = target.textContent || ''
			const closeParenIndex = text.lastIndexOf(')')

			if (closeParenIndex > 0) {
				try {
					range.setStart(target.firstChild, closeParenIndex)
					range.collapse(true)
					selection.addRange(range)
				} catch (e) {
					console.warn('[activeFunction] 커서 설정 실패:', e)
				}
			}
		}
	}

	functionActivePosition(functionPosition)

	const selectorOverlay = useSelectorOverlay()
	selectorOverlay.positionSelector(target)
}

export function overFunctionList(tt: string, t: string) {
	const textList = tt.split(',')
	let test = false
	textList.forEach((item: string) => {
		const textItem = item.split(':')
		if (test) return
		test = textItem.some((text: string) => text === t)
	})

	return test
}

export function clampPositionString(position: string): string {
	return position
		.split(',')
		.map((item: string) => item.trim().toUpperCase())
		.filter((item: string) => item.length > 0)
		.join(',')
}

function shiftCellRef(cellRef: string, type: 'row' | 'col', insertedIndex: number): string {
	const match = cellRef.match(/^([A-Z]+)(\d+)$/)
	if (!match) return cellRef

	const colLabel = match[1]
	const rowNum = parseInt(match[2])

	if (type === 'col') {
		const colIndex = getColumnIndex(colLabel)
		if (colIndex >= insertedIndex) {
			return getColumnLabel(colIndex + 1) + rowNum
		}
	} else {
		if (rowNum >= insertedIndex) {
			return colLabel + (rowNum + 1)
		}
	}

	return cellRef
}

function shiftPositionString(positionStr: string, type: 'row' | 'col', insertedIndex: number): string {
	return positionStr
		.split(',')
		.map((item: string) => {
			const trimmed = item.trim()
			if (trimmed.includes(':')) {
				return trimmed
					.split(':')
					.map((p: string) => shiftCellRef(p.trim(), type, insertedIndex))
					.join(':')
			}
			return shiftCellRef(trimmed, type, insertedIndex)
		})
		.join(',')
}

export function updateFormulaPositions(type: 'row' | 'col', insertedIndex: number) {
	const { tableSelector } = storeToRefs(useEditorStore())
	if (!tableSelector.value) return

	tableSelector.value.forEach((row: any) => {
		row.forEach((cell: any) => {
			if (cell.html?.dataset?.type === 'function' && cell.html?.dataset?.position) {
				const updated = shiftPositionString(cell.html.dataset.position, type, insertedIndex)
				if (updated !== cell.html.dataset.position) {
					cell.html.dataset.position = updated
				}
			}
		})
	})
}

function shrinkCellRef(cellRef: string, type: 'row' | 'col', deletedIndex: number): string | null {
	const match = cellRef.match(/^([A-Z]+)(\d+)$/)
	if (!match) return cellRef

	const colLabel = match[1]
	const rowNum = parseInt(match[2])

	if (type === 'col') {
		const colIndex = getColumnIndex(colLabel)
		if (colIndex === deletedIndex) return null
		if (colIndex > deletedIndex) {
			return getColumnLabel(colIndex - 1) + rowNum
		}
	} else {
		if (rowNum === deletedIndex) return null
		if (rowNum > deletedIndex) {
			return colLabel + (rowNum - 1)
		}
	}

	return cellRef
}

function shrinkPositionString(positionStr: string, type: 'row' | 'col', deletedIndex: number): string {
	return positionStr
		.split(',')
		.map((item: string) => {
			const trimmed = item.trim()
			if (trimmed.includes(':')) {
				const parts = trimmed.split(':')
				const shifted = parts.map((p: string) => shrinkCellRef(p.trim(), type, deletedIndex))
				if (shifted.some((s: string | null) => s === null)) return null
				return shifted.join(':')
			}
			return shrinkCellRef(trimmed, type, deletedIndex)
		})
		.filter((item: string | null) => item !== null)
		.join(',')
}

export function deleteFormulaPositions(type: 'row' | 'col', deletedIndex: number) {
	const { tableSelector } = storeToRefs(useEditorStore())
	if (!tableSelector.value) return

	tableSelector.value.forEach((row: any) => {
		row.forEach((cell: any) => {
			if (cell.html?.dataset?.type === 'function' && cell.html?.dataset?.position) {
				const updated = shrinkPositionString(cell.html.dataset.position, type, deletedIndex)
				cell.html.dataset.position = updated
			}
		})
	})
}
